import { useState } from "react";
import { AlertTriangle, Ban, Loader2, ShieldAlert } from "lucide-react";
import { fmtMoney } from "@/lib/billsLanguage";
import { fmtDate } from "@/components/bills/BillListCard";

// Operator-facing wording for the governance refusals the backend already enforces.
export const VOID_ERRORS = {
  MAKER_CHECKER_VIOLATION: "The person who requested the void cannot approve it.",
  VOID_REQUIRES_GOVERNED_SETTLEMENT_RESOLUTION:
    "This bill has recorded payment or settlement activity. It can't be voided until that settlement is resolved.",
  ACCOUNTS_SETTLEMENT_STATE_UNAVAILABLE:
    "Accounts settlement status is temporarily unavailable. The bill has not been voided.",
  ACCOUNTS_SETTLEMENT_CREDENTIAL_REJECTED: "Accounts can't be reached right now. The bill has not been voided.",
  ACCOUNTS_SETTLEMENT_BILL_NOT_FOUND: "Accounts can't confirm this bill right now. The bill has not been voided.",
  ACCOUNTS_SETTLEMENT_IDENTITY_MISMATCH: "Accounts can't confirm this bill right now. The bill has not been voided.",
  ACCOUNTS_VOID_STATE_AHEAD_OF_BILLS: "Accounts has already recorded a void for this bill. Please contact support.",
  ACCOUNTS_SETTLEMENT_NOT_CONFIGURED: "Accounts can't be reached right now. The bill has not been voided.",
  void_already_requested: "Someone has already requested a void for this bill.",
  already_voided: "This bill is already voided.",
  reason_required: "Please add a reason for the void.",
  not_confirmed: "Only a confirmed bill can be voided.",
};

export const voidErrorText = (res) =>
  (res?.status === 403 && !VOID_ERRORS[res?.data?.error_code] ? "You don't have permission to void bills." : null) ||
  VOID_ERRORS[res?.data?.error_code] || res?.data?.message || "That didn't go through. Please try again.";

const Field = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-[#E4E0D8] last:border-0">
    <span className="text-[13px] text-archival-secondary">{label}</span>
    <span className="text-[14px] text-archival-primary text-right break-words" data-testid={testId}>{value ?? "—"}</span>
  </div>
);

const Sheet = ({ title, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/40 px-0 sm:px-4"
    role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
    <div className="w-full sm:max-w-[480px] rounded-t-2xl sm:rounded-xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] max-h-[88vh] overflow-y-auto">
      {children}
    </div>
  </div>
);

const stamp = (ts) => (ts ? new Date(ts).toLocaleString("en-IN") : "—");

/** CONFIRMED bill: a separated, deliberately quiet destructive action. */
export const VoidAction = ({ onOpen }) => (
  <div className="mt-8 border-t border-[#E4E0D8] pt-4">
    <button type="button" onClick={onOpen} data-testid="bill-void-open"
      className="w-full min-h-[48px] rounded-md border border-red-300 bg-white px-4 text-[15px] font-medium text-red-800 inline-flex items-center justify-center gap-2 transition-colors hover:bg-red-50 active:bg-red-100">
      <Ban size={16} />Void bill
    </button>
    <p className="mt-2 text-center text-[12px] text-archival-secondary">
      A different authorised user must approve a void request.
    </p>
  </div>
);

export const RequestVoidSheet = ({ d, busy, error, onSubmit, onCancel }) => {
  const [reason, setReason] = useState("");
  return (
    <Sheet title="Void this bill?" testId="bill-void-request-sheet">
      <h2 className="text-[18px] font-semibold text-archival-primary">Void this bill?</h2>
      <dl className="mt-3 rounded-md border border-[#E4E0D8] px-3">
        <Field label="Vendor" value={d.vendor_name} testId="void-sheet-vendor" />
        <Field label="Bill number" value={d.bill_number} testId="void-sheet-number" />
        <Field label="Bill date" value={fmtDate(d.bill_date)} testId="void-sheet-date" />
        <Field label="Total" value={fmtMoney(d.total_amount)} testId="void-sheet-total" />
      </dl>
      <p className="mt-3 text-[13px] text-archival-secondary" data-testid="void-sheet-explainer">
        This does not delete the bill. The bill and its evidence stay in history. A different authorised user must
        approve the request before the bill is voided.
      </p>
      <label className="mt-4 block text-[13px] font-medium text-archival-primary" htmlFor="void-reason">Reason for void</label>
      <textarea id="void-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
        data-testid="void-reason-input" placeholder="Why is this bill being voided?"
        className="mt-1 w-full rounded-md border border-[#E4E0D8] p-3 text-[15px]" />
      {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="bill-void-error">{error}</p>}
      <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} data-testid="void-cancel"
          className="min-h-[48px] rounded-md border border-[#E4E0D8] px-4 text-[15px]">Cancel</button>
        <button type="button" onClick={() => onSubmit(reason)} disabled={busy || !reason.trim()} data-testid="void-submit"
          className="min-h-[48px] rounded-md bg-red-700 px-4 text-[15px] font-medium text-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />}Request void
        </button>
      </div>
    </Sheet>
  );
};

export const ApproveVoidSheet = ({ d, busy, error, onSubmit, onCancel }) => (
  <Sheet title="Approve void?" testId="bill-void-approve-sheet">
    <h2 className="text-[18px] font-semibold text-archival-primary">Approve void?</h2>
    <dl className="mt-3 rounded-md border border-[#E4E0D8] px-3">
      <Field label="Vendor" value={d.vendor_name} />
      <Field label="Bill number" value={d.bill_number} />
      <Field label="Total" value={fmtMoney(d.total_amount)} />
      <Field label="Reason" value={d.void?.reason} testId="approve-sheet-reason" />
      <Field label="Requested by" value={d.void?.requested_by} testId="approve-sheet-requested-by" />
    </dl>
    <p className="mt-3 flex gap-2 text-[13px] text-amber-900 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
      data-testid="approve-sheet-warning">
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      Approving withdraws this bill from Inventory and Accounts. The original bill and its evidence stay available.
    </p>
    {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="bill-void-error">{error}</p>}
    <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
      <button type="button" onClick={onCancel} disabled={busy} data-testid="void-approve-cancel"
        className="min-h-[48px] rounded-md border border-[#E4E0D8] px-4 text-[15px]">Cancel</button>
      <button type="button" onClick={onSubmit} disabled={busy} data-testid="void-approve-submit"
        className="min-h-[48px] rounded-md bg-red-700 px-4 text-[15px] font-medium text-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy && <Loader2 size={15} className="animate-spin" />}Approve void
      </button>
    </div>
  </Sheet>
);

export const RejectVoidSheet = ({ busy, error, onSubmit, onCancel }) => {
  const [reason, setReason] = useState("");
  return (
    <Sheet title="Reject this void request?" testId="bill-void-reject-sheet">
      <h2 className="text-[18px] font-semibold text-archival-primary">Reject this void request?</h2>
      <p className="mt-2 text-[13px] text-archival-secondary">The bill goes back to confirmed and keeps its current effect.</p>
      <label className="mt-4 block text-[13px] font-medium text-archival-primary" htmlFor="reject-reason">Reason (optional)</label>
      <textarea id="reject-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
        data-testid="void-reject-reason" className="mt-1 w-full rounded-md border border-[#E4E0D8] p-3 text-[15px]" />
      {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="bill-void-error">{error}</p>}
      <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} data-testid="void-reject-cancel"
          className="min-h-[48px] rounded-md border border-[#E4E0D8] px-4 text-[15px]">Keep the request</button>
        <button type="button" onClick={() => onSubmit(reason)} disabled={busy} data-testid="void-reject-submit"
          className="min-h-[48px] rounded-md border border-red-300 px-4 text-[15px] font-medium text-red-800 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />}Reject request
        </button>
      </div>
    </Sheet>
  );
};

/** VOID_PENDING / VOIDED: who asked, when, why, and who decided. */
export const VoidGovernanceCard = ({ d, busy, error, onApprove, onReject }) => {
  const v = d.void || {};
  const pending = d.status === "VOID_PENDING";
  return (
    <section className="mt-6 rounded-md border border-red-200 bg-white p-4" data-testid="bill-void-card">
      <p className="flex items-center gap-2 text-[14px] font-semibold text-red-800">
        <ShieldAlert size={16} />{pending ? "Void requested" : "Bill voided"}
      </p>
      <dl className="mt-2">
        <Field label="Requested by" value={v.requested_by} testId="void-card-requested-by" />
        <Field label="Requested at" value={stamp(v.requested_at)} testId="void-card-requested-at" />
        <Field label="Reason" value={v.reason} testId="void-card-reason" />
        {!pending && <Field label="Approved by" value={v.approved_by} testId="void-card-approved-by" />}
        {!pending && <Field label="Approved at" value={stamp(v.approved_at)} testId="void-card-approved-at" />}
        <Field label="Status" value={pending ? "Awaiting approval" : "Voided"} testId="void-card-status" />
        <Field label="Request ID" value={v.void_request_id} testId="void-card-request-id" />
      </dl>
      {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="bill-void-error">{error}</p>}
      {pending && v.viewer_is_requester && !d.can_approve_void && (
        <p className="mt-3 text-[13px] text-archival-secondary" data-testid="void-awaiting-approval">
          Awaiting approval from another authorised user.
        </p>
      )}
      {pending && (d.can_approve_void || d.can_reject_void) && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          {d.can_approve_void && (
            <button type="button" onClick={onApprove} disabled={busy} data-testid="bill-void-approve-open"
              className="min-h-[48px] flex-1 rounded-md bg-red-700 px-4 text-[15px] font-medium text-white disabled:opacity-50">
              Approve void
            </button>
          )}
          {d.can_reject_void && (
            <button type="button" onClick={onReject} disabled={busy} data-testid="bill-void-reject-open"
              className="min-h-[48px] flex-1 rounded-md border border-[#E4E0D8] px-4 text-[15px] font-medium text-archival-primary disabled:opacity-50">
              Reject
            </button>
          )}
        </div>
      )}
      <p className="mt-3 text-[12px] text-archival-secondary">The bill and its evidence are never deleted.</p>
    </section>
  );
};
